import React, { memo, useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Avatar from "../../assets/images/icon/placeholder_Awatar.png";
import { _imagesURL } from "../../constants/API";
import "./ProfileAvatar.css";

/**
 * ProfileAvatar — Standardized Profile Avatar Component
 * Ensures exact 1:1 circular ratio, anti-distortion object-cover,
 * and reliable image loading with a sleek shimmer & spinner loader.
 *
 * @param {string} src - Raw image path or full URL.
 * @param {number|string} size - Avatar dimension in px (e.g. 38, 44, 50). Default is 40.
 * @param {'gradient'|'subtle'|'cyan'|'plain'} variant - Ring style. Default is 'gradient'.
 * @param {boolean} isOnline - Show online indicator dot. Default is false.
 * @param {string} link - Optional route URL to wrap in a Link.
 * @param {string} alt - Alt text. Default is "Profile".
 * @param {string} className - Additional CSS class for outer container.
 * @param {object} style - Additional inline styles for outer container.
 * @param {function} onClick - Click handler.
 */
function ProfileAvatar({
  src,
  size = 40,
  variant = "gradient",
  isOnline = false,
  link,
  alt = "Profile",
  className = "",
  style = {},
  onClick,
}) {
  // Resolve image source with CDN fallback
  const resolvedSrc = useMemo(() => {
    if (!src || src === Avatar) return Avatar;
    if (typeof src !== "string") return Avatar;
    const trimmed = src.trim();
    if (!trimmed || trimmed === "undefined" || trimmed === "null") return Avatar;
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("data:") ||
      trimmed.startsWith("blob:") ||
      trimmed.includes("placeholder_Awatar") ||
      trimmed.includes("assets/") ||
      trimmed.includes("@fs") ||
      trimmed.includes("node_modules")
    ) {
      return trimmed;
    }
    // Clean base and path to guarantee exactly one slash between domain and path
    const base = _imagesURL.trim().replace(/\/+$/, "");
    const path = trimmed.replace(/^\/+/, "");
    return `${base}/${path}`;
  }, [src]);

  const isLocalPlaceholder = resolvedSrc === Avatar || resolvedSrc.includes("placeholder_Awatar");
  const [isLoaded, setIsLoaded] = useState(isLocalPlaceholder);
  const [imgSrc, setImgSrc] = useState(resolvedSrc);
  const imgRef = useRef(null);

  useEffect(() => {
    setImgSrc(resolvedSrc);
    if (resolvedSrc === Avatar || resolvedSrc.includes("placeholder_Awatar")) {
      setIsLoaded(true);
    } else if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setIsLoaded(true);
    } else {
      setIsLoaded(false);
    }
  }, [resolvedSrc]);

  const numericSize = typeof size === "number" ? size : parseInt(size, 10) || 40;
  const sizeStyle = {
    width: `${numericSize}px`,
    height: `${numericSize}px`,
    minWidth: `${numericSize}px`,
    minHeight: `${numericSize}px`,
  };

  const variantClass = `mr-profile-avatar-wrap--${variant}`;

  const avatarContent = (
    <div
      className={`mr-profile-avatar-wrap ${variantClass} ${className}`}
      style={{ ...sizeStyle, ...style }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="mr-profile-avatar-inner">
        {!isLoaded && (
          <div className="mr-avatar-shimmer" aria-hidden="true">
            {numericSize >= 32 && <div className="mr-avatar-shimmer-spinner" />}
          </div>
        )}
        <img
          ref={imgRef}
          src={imgSrc}
          alt={alt}
          className="rounded-circle h-100 w-100 object-cover"
          style={{
            width: "100%",
            height: "100%",
            aspectRatio: "1 / 1",
            objectFit: "cover",
            borderRadius: "50%",
            display: "block",
            opacity: isLoaded ? 1 : 0,
            transition: "opacity 0.2s ease-in-out",
          }}
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            if (imgSrc !== Avatar && !imgSrc.includes("placeholder_Awatar")) {
              setImgSrc(Avatar);
            }
            setIsLoaded(true);
          }}
          loading="eager"
        />
      </div>
      {isOnline && <span className="mr-avatar-status-dot" title="Online" />}
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="mr-profile-avatar-root" onClick={onClick}>
        {avatarContent}
      </Link>
    );
  }

  return avatarContent;
}

export default memo(ProfileAvatar);
